import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../cn";
import { CopyButton } from "../secret";

/** Buttons used in website tab headers. Theme tokens only, so they follow each app's accent. */
export const tabPrimaryClass = "inline-flex h-[34px] shrink-0 items-center gap-[7px] rounded-lg bg-accent px-[14px] text-[12.5px] font-semibold text-accent-fg shadow-sm transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60";
export const tabSecondaryClass = "inline-flex h-[34px] shrink-0 items-center gap-[7px] rounded-lg border border-line-strong bg-surface px-[12px] text-[12.5px] font-medium text-fg transition hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50";

/** A website tab section: icon tile, title with optional count, one-line description and an action on the right. */
export function TabCard({ icon: Icon, logo, title, count, description, action, flush, className, children }: {
  icon?: LucideIcon; logo?: ReactNode; title: string; count?: number; description?: ReactNode; action?: ReactNode; flush?: boolean; className?: string; children: ReactNode;
}) {
  return (
    <section className={cn("overflow-hidden rounded-xl border border-line bg-surface shadow-sm", className)} aria-label={title}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-b border-line px-[16px] py-[13px]">
        {(logo || Icon) && (
          <span className="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent max-sm:hidden">
            {logo ?? (Icon && <Icon size={20} strokeWidth={1.8} />)}
          </span>
        )}
        <div className="min-w-0 flex-[1_1_14rem] leading-tight">
          <h2 className="flex items-center gap-2 text-[14.5px] font-bold">
            {title}
            {count != null && <span className="rounded-full bg-surface-3 px-2 py-0.5 text-[11px] font-semibold text-muted tabular-nums">{count}</span>}
          </h2>
          {description && <div className="mt-[4px] text-[12px] leading-[17px] text-muted">{description}</div>}
        </div>
        {action && <div className="flex flex-wrap items-center gap-2">{action}</div>}
      </div>
      <div className={flush ? undefined : "px-[16px] py-[14px]"}>{children}</div>
    </section>
  );
}

/** A labelled value tile (host, username, socket…) with an optional copy button. */
export function InfoTile({ label, value, copy, mono = true }: { label: string; value: ReactNode; copy?: string | null; mono?: boolean }) {
  return (
    <div className="flex min-w-0 items-center gap-2 rounded-lg border border-line bg-surface-2/50 px-[12px] py-[9px]">
      <div className="min-w-0 flex-1 leading-tight">
        <p className="text-[10.5px] tracking-wide text-subtle uppercase">{label}</p>
        <div className={cn("mt-[4px] truncate text-[12.5px] text-fg", mono && "font-mono")}>{value}</div>
      </div>
      {copy && <CopyButton value={copy} />}
    </div>
  );
}
